import { CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Title, Tooltip, Filler } from 'chart.js';
import classNames from 'classnames';
import { useEffect, useState, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import styles from './style.module.scss';
import { useSelector } from 'react-redux';
import { themeEnum } from 'shared/utils/Enum';
import { CaretIcon } from 'assets';
import DropDownOptions from 'shared/components/common/dropdown/dropdown_option/DropdownOption';
import { optionsDateRange } from 'shared/utils/IntegrationConstant';
import { dateRangesEnum } from 'shared/utils/Enum';
import { startOfDay, endOfDay, subDays, startOfWeek, subMonths, startOfMonth, subQuarters, startOfQuarter, subYears, startOfYear } from 'date-fns';
import moment from 'moment';

const CustomLineChart = ({ name, ChartWraper, labels, data1, data2, currentView, onViewChange }) => {
    ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    const [openSelection, setOpenSelection] = useState(false);
    const dropDownRef = useRef(null);

    const defaultLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
    const defaultData1 = [15, 20, 35, 30, 50, 45, 60, 55, 75, 80];
    const defaultData2 = [10, 30, 25, 45, 35, 55, 40, 70, 50, 65];

    const finalLabels = labels && labels.length > 0 ? labels : defaultLabels;
    const finalData1 = data1 && data1.length > 0 ? data1 : defaultData1;
    const finalData2 = data2 && data2.length > 0 ? data2 : defaultData2;

    const chartViewOptions = [
        { title: 'Monthly' },
        { title: 'Yearly' }
    ];

    useEffect(() => {
        ChartJS.defaults.color = themeEnum.Light === theme ? '#5B5B5B' : 'white';
    }, [theme]);

    // Premium Chart Options
    const premiumOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(0,0,0,0.8)',
                padding: 12,
                titleFont: { size: 13 },
                bodyFont: { size: 12 },
                cornerRadius: 8,
                displayColors: false
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: {
                    font: { size: 11 },
                    color: '#9ca3af'
                }
            },
            y: {
                grid: {
                    borderDash: [5, 5],
                    color: 'rgba(0,0,0,0.05)',
                    drawBorder: false
                },
                ticks: {
                    font: { size: 11 },
                    color: '#9ca3af',
                    callback: (value) => {
                        if (value >= 1000000) return `Rs. ${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `Rs. ${(value / 1000).toFixed(1)}k`;
                        return `Rs. ${value}`;
                    }
                },
                border: { display: false }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
    };

    return (
        <div className={classNames(`${theme}SummaryContainer`, ChartWraper && ChartWraper, styles.chartContainer, 'py-4 px-3 d-flex flex-column gap-3')}>
            <div className={classNames('d-flex w-100 justify-content-between align-items-center px-3')}>
                <label className={classNames(styles.chartTitle, `${theme}FontColor`)}>{name}</label>
                <div
                    className={classNames('d-flex align-items-center justify-content-center gap-2 position-relative')}
                    role={'button'}
                    ref={dropDownRef}
                    onClick={() => setOpenSelection(!openSelection)}
                >
                    <label className={classNames(styles.dateLabel, `${theme}FontColor`)} role="button">
                        {currentView || 'Select View'}
                    </label>
                    <CaretIcon className={classNames(styles.caretIcon, `${theme}SvgIcon`)} />
                    <DropDownOptions
                        id={`${name?.replace(/\s+/g, '')}Dropdown`}
                        openSelection={openSelection}
                        setOpenSelection={setOpenSelection}
                        options={chartViewOptions}
                        selectedItem={(val) => onViewChange?.(val)}
                        ref={dropDownRef}
                        customContainer={styles.dropdownContainer}
                    />
                </div>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <Line
                    options={premiumOptions}
                    data={{
                        labels: finalLabels,
                        datasets: [
                            {
                                label: 'Current',
                                data: finalData1,
                                borderColor: '#4A86F7',
                                backgroundColor: (context) => {
                                    const ctx = context.chart.ctx;
                                    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                                    gradient.addColorStop(0, 'rgba(74, 134, 247, 0.4)');
                                    gradient.addColorStop(1, 'rgba(74, 134, 247, 0.0)');
                                    return gradient;
                                },
                                borderWidth: 3,
                                pointRadius: 0,
                                pointHoverRadius: 6,
                                fill: true,
                                tension: 0.4
                            },
                            {
                                label: 'Previous',
                                data: finalData2,
                                borderColor: '#e5e7eb',
                                borderDash: [5, 5],
                                borderWidth: 2,
                                pointRadius: 0,
                                pointHoverRadius: 4,
                                tension: 0.4,
                                fill: false,
                                hidden: !data2
                            }
                        ]
                    }}
                />
            </div>
        </div>
    );
};

export default CustomLineChart;

